import React, { useState, useEffect, useRef } from "react";
import {
  FolderKanban,
  CalendarCheck,
  ClipboardCheck,
  Globe,
  Download,
  Landmark,
  CheckCircle,
  TrendingUp,
  Award,
  BarChart3,
  AlertCircle,
  Star,
  Target,
  PieChart as PieChartIcon,
  FileText,
  Eye
} from "lucide-react";
import Button from "components/ui/Button";
import MetricCard from "components/dashboard/MetricCard";
import DevelopmentDimensionPieCard from "components/dashboard/DevelopmentDimensionPieCard";
import { useLocationContext } from "context/LocationContext";
import { formatIgfCurrency, getNationalDashboardData, YEARS } from "utils/dashboardData";
import {
  classifyPerformance,
  DPAT_PERFORMANCE_TIER_RANGES,
} from "utils/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  ComposedChart,
  Line
} from "recharts";
import { getConsolidatedAssessmentsByYear } from "service/contract-assessment.service";
import { getRegions } from "utils/utils";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Get performance color based on classification
function getPerformanceColor(classification) {
  switch (classification) {
    case "Excellent": return "text-green-600";
    case "Very Good": return "text-emerald-500";
    case "Good": return "text-blue-500";
    case "Satisfactory": return "text-yellow-600";
    default: return "text-red-500";
  }
}

// Get performance badge class
function getPerformanceBadgeClass(classification) {
  switch (classification) {
    case "Excellent": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    case "Very Good": return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300";
    case "Good": return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
    case "Satisfactory": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    default: return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
  }
}

export default function NationalDashboard() {
  // Automatically select current year - 1
  const currentYear = new Date().getFullYear();
  const defaultYear = String(currentYear - 1);

  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [loading, setLoading] = useState(true);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [assessmentError, setAssessmentError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const dashboardRef = useRef(null);
  const { orgTree } = useLocationContext();

  // Get regions and create lookup map
  const regionsList = getRegions();
  const regionsMap = regionsList.reduce((map, region) => {
    map[region.id] = region.displayName;
    return map;
  }, {});

  // Helper function to get region name from code
  const getRegionName = (regionCode) => {
    return regionsMap[regionCode] || regionCode;
  };

  // Note: You'll need to define districtsMap similarly if you have district data
  // For now, using a placeholder or remove district name mapping
  const getDistrictName = (districtCode) => {
    // If you have a districts lookup, implement it here
    // For now, return the code itself
    return districtCode;
  };

  // Fetch assessment data directly
  useEffect(() => {
    const fetchAssessmentData = async () => {
      setAssessmentLoading(true);
      setAssessmentError(null);
      try {
        const response = await getConsolidatedAssessmentsByYear(selectedYear);
        const consolidatedData = response?.data;

        if (consolidatedData && consolidatedData.regions && consolidatedData.regions.length > 0) {
          setAssessmentData(consolidatedData);
        } else {
          setAssessmentData(null);
        }
      } catch (error) {
        console.error("Error fetching assessment data:", error);
        setAssessmentError(error);
        setAssessmentData(null);
      } finally {
        setAssessmentLoading(false);
      }
    };

    fetchAssessmentData();
  }, [selectedYear]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await getNationalDashboardData(orgTree, selectedYear);
        setDashboardData(data);
      } catch (error) {
        console.error("Error loading national dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [orgTree, selectedYear]);

  const parseAssessmentData = () => {
    if (!assessmentData) return null;
    if (!assessmentData.regions || assessmentData.regions.length === 0) return null;
    return assessmentData;
  };

  const parsedAssessmentData = parseAssessmentData();
  const hasAssessmentData = parsedAssessmentData !== null && parsedAssessmentData.totalRegions > 0;

  // Prepare assessment metrics for display
  const getAssessmentMetrics = () => {
    if (!hasAssessmentData) return null;

    const data = parsedAssessmentData;

    const sortedRegions = [...data.regions].sort((a, b) => b.totalScore - a.totalScore);
    const topPerformer = sortedRegions[0];
    const topScore = topPerformer?.totalScore || 0;
    const bottomPerformer = sortedRegions[sortedRegions.length - 1];
    const bottomScore = bottomPerformer?.totalScore || 0;

    const scores = data.regions.map(r => r.totalScore);
    const averageScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    const medianScore = [...scores].sort((a, b) => a - b)[Math.floor(scores.length / 2)];

    // Performance distribution using the classification function
    const distribution = {
      Excellent: data.regions.filter(r => classifyPerformance(r.totalScore) === "Excellent").length,
      VeryGood: data.regions.filter(r => classifyPerformance(r.totalScore) === "Very Good").length,
      Good: data.regions.filter(r => classifyPerformance(r.totalScore) === "Good").length,
      Satisfactory: data.regions.filter(r => classifyPerformance(r.totalScore) === "Satisfactory").length,
      Unsatisfactory: data.regions.filter(r => classifyPerformance(r.totalScore) === "Unsatisfactory").length
    };

    const totalDistricts = data.regions.reduce((sum, region) => sum + (region.totalDistrict || 0), 0);

    let topDistrict = null;
    let topDistrictScore = 0;
    data.regions.forEach(region => {
      region.districts?.forEach(district => {
        if (district.totalScore > topDistrictScore) {
          topDistrictScore = district.totalScore;
          topDistrict = {
            ...district,
            regionCode: region.regionCode
          };
        }
      });
    });

    const maxScore = Math.max(...scores);
    const minScore = Math.min(...scores);
    const scoreRange = maxScore - minScore;

    const sortedScores = [...scores].sort((a, b) => b - a);
    const top25Threshold = sortedScores[Math.floor(sortedScores.length * 0.25)];
    const bottom25Threshold = sortedScores[Math.floor(sortedScores.length * 0.75)];

    const tierBreakdown = {
      top25: scores.filter(s => s >= top25Threshold).length,
      mid50: scores.filter(s => s < top25Threshold && s >= bottom25Threshold).length,
      bottom25: scores.filter(s => s < bottom25Threshold).length
    };

    return {
      year: data.year,
      totalRegions: data.totalRegions,
      totalDistricts,
      nationalAverage: data.nationalAverage || averageScore,
      averageScore,
      medianScore,
      topPerformer: {
        ...topPerformer,
        name: getRegionName(topPerformer?.regionCode)
      },
      topScore,
      bottomPerformer: {
        ...bottomPerformer,
        name: getRegionName(bottomPerformer?.regionCode)
      },
      bottomScore,
      maxScore,
      minScore,
      scoreRange,
      distribution,
      tierBreakdown,
      topDistrict: topDistrict ? {
        ...topDistrict,
        name: getDistrictName(topDistrict.districtCode),
        regionName: getRegionName(topDistrict.regionCode)
      } : null,
      topDistrictScore,
      regions: data.regions.map(region => ({
        ...region,
        name: getRegionName(region.regionCode),
        districts: region.districts?.map(district => ({
          ...district,
          name: getDistrictName(district.districtCode)
        }))
      })),
      rawData: data
    };
  };

  const assessmentMetrics = getAssessmentMetrics();

  const getAssessmentRegionData = () => {
    if (!hasAssessmentData || !assessmentMetrics) return [];
    return assessmentMetrics.regions
      .map(region => ({
        name: region.name,
        code: region.regionCode,
        score: region.totalScore,
        rank: region.regionRank,
        classification: classifyPerformance(region.totalScore),
        districts: region.totalDistrict
      }))
      .sort((a, b) => b.score - a.score);
  };

  const assessmentRegionData = getAssessmentRegionData();

  // PDF Export Function
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;

    setIsExporting(true);

    try {
      // Show loading toast or notification
      const element = dashboardRef.current;

      // Set temporary styles for better PDF capture
      const originalOverflow = element.style.overflow;
      element.style.overflow = 'visible';

      // Capture the dashboard
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: false
      });

      // Restore original overflow
      element.style.overflow = originalOverflow;

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.height;

      // Add subsequent pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdf.internal.pageSize.height;
      }

      // Save the PDF
      pdf.save(`national-dashboard-${selectedYear}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Preview PDF Function
  const previewPDF = async () => {
    if (!dashboardRef.current) return;

    setIsExporting(true);
    setShowPreview(true);

    try {
      const element = dashboardRef.current;
      const originalOverflow = element.style.overflow;
      element.style.overflow = 'visible';

      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: false
      });

      element.style.overflow = originalOverflow;

      const imgData = canvas.toDataURL('image/png');
      setPreviewUrl(imgData);

    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Failed to generate preview. Please try again.');
      setShowPreview(false);
    } finally {
      setIsExporting(false);
    }
  };

  // Close preview modal
  const closePreview = () => {
    setShowPreview(false);
    setPreviewUrl(null);
  };

  // Download from preview
  const downloadFromPreview = () => {
    if (previewUrl) {
      const link = document.createElement('a');
      link.download = `national-dashboard-${selectedYear}.png`;
      link.href = previewUrl;
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="main-content flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto"></div>
          <p className="text-sm text-muted-foreground mt-2">Loading dashboard for {selectedYear}...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) return null;

  const activityPlanData = dashboardData.metrics.activityPlan.regionalData || [];
  const projectsData = dashboardData.metrics.projects.regionalData || [];
  const programmesData = dashboardData.metrics.programmes.regionalData || [];
  const meetingsData = dashboardData.metrics.meetings.regionalData || [];
  const igfData = dashboardData.metrics.igfCollected.regionalData || [];
  const planVsExecutedData = dashboardData.planVsExecutedData || [];
  const projectsByDimension = dashboardData.projectsByDimension || [];
  const aapByDimension = dashboardData.aapByDimension || [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
              {entry.name === "Execution Rate" && "%"}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const ProjectsPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = projectsByDimension.reduce((sum, d) => sum + d.value, 0);
      const percentage = ((data.value / total) * 100).toFixed(1);
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg max-w-xs">
          <p className="font-semibold text-sm mb-1">{data.name}</p>
          <div className="space-y-1 text-xs">
            <p className="text-muted-foreground">
              Count: <span className="font-medium text-foreground">{data.value}</span>
            </p>
            <p className="text-muted-foreground">
              Percentage: <span className="font-medium text-foreground">{percentage}%</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const AAPPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = aapByDimension.reduce((sum, d) => sum + d.value, 0);
      const percentage = ((data.value / total) * 100).toFixed(1);
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg max-w-xs">
          <p className="font-semibold text-sm mb-1">{data.name}</p>
          <div className="space-y-1 text-xs">
            <p className="text-muted-foreground">
              Count: <span className="font-medium text-foreground">{data.value}</span>
            </p>
            <p className="text-muted-foreground">
              Percentage: <span className="font-medium text-foreground">{percentage}%</span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className="main-content space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Globe className="h-6 w-6 text-accent" />
              National Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {dashboardData.subtitle}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-medium text-foreground">
                Yearly View - {dashboardData.year}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="
                appearance-none
                rounded-xl
                border border-slate-300
                bg-white
                px-4
                py-2.5
                pr-10
                text-sm
                font-medium
                text-slate-700
                shadow-sm
                transition-all
                duration-200
                hover:border-slate-400
                focus:border-blue-500
                focus:outline-none
                focus:ring-4
                focus:ring-blue-100
                dark:border-slate-700
                dark:bg-slate-900
                dark:text-slate-200
                dark:hover:border-slate-600
                dark:focus:border-blue-400
                dark:focus:ring-blue-900/30
              "
            >
              {YEARS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={previewPDF}
              disabled={isExporting}
            >
              <Eye className="h-4 w-4 mr-1" /> Preview
            </Button>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={exportToPDF}
              disabled={isExporting}
            >
              {isExporting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent mr-1"></div>
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Export PDF
            </Button>
          </div>
        </div>

        {/* Dashboard Content - wrapped in ref for PDF capture */}
        <div ref={dashboardRef}>
          {/* Top 6 Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <MetricCard
              title="Activity Plans"
              value={dashboardData.metrics.activityPlan.value}
              icon={ClipboardCheck}
              status="info"
            />
            <MetricCard
              title="Projects"
              value={dashboardData.metrics.projects.value}
              icon={FolderKanban}
              status="success"
            />
            <MetricCard
              title="Programmes"
              value={dashboardData.metrics.programmes.value}
              icon={CalendarCheck}
              status="warning"
            />
            <MetricCard
              title="Activity Executed"
              value={dashboardData.metrics.activityExecuted.value}
              subMetrics={dashboardData.metrics.activityExecuted.subMetrics}
              icon={CheckCircle}
              status="success"
              footer={
                <div className="mt-2 pt-2 border-t border-border">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">Execution Rate:</span>
                    <span className="font-semibold text-green-600">
                      {dashboardData.metrics.activityExecuted.executionRate}%
                    </span>
                  </div>
                </div>
              }
            />
            <MetricCard
              title="Meetings"
              value={dashboardData.metrics.meetings.value}
              icon={CalendarCheck}
              status="success"
            />
            <MetricCard
              title="IGF Collected"
              value={dashboardData.metrics.igfCollected.value}
              icon={Landmark}
              status="info"
            />
          </div>

          {/* Assessment Metrics Section */}
           <div className="metric-card mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-accent" />
                <h3 className="font-semibold text-sm">Contract Performance Assessment Overview {hasAssessmentData && `- ${assessmentMetrics?.year}`}</h3>
              </div>
              {assessmentLoading && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-accent"></div>
                  <span>Loading assessment data...</span>
                </div>
              )}
            </div>

            {assessmentError ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="bg-red-100 dark:bg-red-900/20 rounded-full p-3 mb-3">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
                <p className="text-sm text-muted-foreground">Error loading assessment data</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Please try again later</p>
              </div>
            ) : !hasAssessmentData && !assessmentLoading ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="bg-muted/30 rounded-full p-3 mb-3">
                  <BarChart3 className="h-8 w-8 text-muted-foreground/50" />
                </div>
                <p className="text-sm text-muted-foreground">No assessment data available for {selectedYear}</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Complete assessments to view performance metrics</p>
              </div>
            ) : hasAssessmentData && assessmentMetrics ? (
              <>
               
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-primary/5 to-transparent rounded-lg p-4 border border-border/50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-muted-foreground">National Average Score</p>
                      <Target className="h-4 w-4 text-accent" />
                    </div>
                    <p className={`text-2xl font-bold ${getPerformanceColor(classifyPerformance(assessmentMetrics.nationalAverage))}`}>
                      {assessmentMetrics.nationalAverage.toFixed(1)}%
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Classification: <span className={`font-medium ${getPerformanceColor(classifyPerformance(assessmentMetrics.nationalAverage))}`}>
                        {classifyPerformance(assessmentMetrics.nationalAverage)}
                      </span>
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-muted-foreground">Median: {assessmentMetrics.medianScore.toFixed(1)}%</span>
                      <span className="text-[10px] text-muted-foreground">|</span>
                      <span className="text-[10px] text-muted-foreground">Range: {assessmentMetrics.scoreRange.toFixed(1)}%</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500/5 to-transparent rounded-lg p-4 border border-border/50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-muted-foreground">Top Performer</p>
                      <Star className="h-4 w-4 text-yellow-500" />
                    </div>
                    <p className="text-sm font-semibold truncate" title={assessmentMetrics.topPerformer?.name}>
                      {assessmentMetrics.topPerformer?.name || "N/A"}
                    </p>
                    <p className={`text-lg font-bold ${getPerformanceColor(classifyPerformance(assessmentMetrics.topScore))}`}>
                      {assessmentMetrics.topScore.toFixed(1)}%
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {classifyPerformance(assessmentMetrics.topScore)}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-orange-500/5 to-transparent rounded-lg p-4 border border-border/50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-muted-foreground">Needs Improvement</p>
                      <AlertCircle className="h-4 w-4 text-orange-500" />
                    </div>
                    <p className="text-sm font-semibold truncate" title={assessmentMetrics.bottomPerformer?.name}>
                      {assessmentMetrics.bottomPerformer?.name || "N/A"}
                    </p>
                    <p className={`text-lg font-bold ${getPerformanceColor(classifyPerformance(assessmentMetrics.bottomScore))}`}>
                      {assessmentMetrics.bottomScore.toFixed(1)}%
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {classifyPerformance(assessmentMetrics.bottomScore)}
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-500/5 to-transparent rounded-lg p-4 border border-border/50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-muted-foreground">Assessment Coverage</p>
                      <PieChartIcon className="h-4 w-4 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold">{assessmentMetrics.totalRegions}</p>
                    <p className="text-xs text-muted-foreground">Regions Assessed</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {assessmentMetrics.totalDistricts} districts total
                    </p>
                  </div>
                </div>

               
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  <div className="bg-muted/20 rounded-lg p-4">
                    <p className="text-xs font-medium text-muted-foreground mb-3">Performance Distribution by Region</p>
                    <div className="space-y-2">
                      {DPAT_PERFORMANCE_TIER_RANGES.map((tier, tierIndex) => {
                        const distKey =
                          tier.classification === "Very Good"
                            ? "VeryGood"
                            : tier.classification;
                        const count = assessmentMetrics.distribution[distKey] ?? 0;
                        const barColors = [
                          "bg-green-600",
                          "bg-emerald-500",
                          "bg-blue-500",
                          "bg-yellow-600",
                          "bg-red-500",
                        ];
                        return (
                          <React.Fragment key={tier.classification}>
                            <div
                              className={`flex items-center justify-between text-xs ${tierIndex > 0 ? "mt-2" : ""}`}
                            >
                              <span className="flex items-center gap-2">
                                <div
                                  className={`w-3 h-3 rounded-full ${barColors[tierIndex] || "bg-muted"}`}
                                />
                                <span>{tier.label}</span>
                              </span>
                              <span className="font-semibold">{count}</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div
                                className={`${barColors[tierIndex] || "bg-muted"} h-2 rounded-full transition-all`}
                                style={{
                                  width: `${(count / assessmentMetrics.totalRegions) * 100}%`,
                                }}
                              />
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>

               
                  <div className="space-y-4">
                    {assessmentMetrics.topDistrict && (
                      <div className="bg-gradient-to-br from-amber-500/5 to-transparent rounded-lg p-4 border border-border/50">
                        <div className="flex items-center gap-2 mb-2">
                          <Award className="h-4 w-4 text-amber-500" />
                          <p className="text-xs font-medium text-muted-foreground">National Top Performing District</p>
                        </div>
                        <p className="text-sm font-semibold">{assessmentMetrics.topDistrict.name}</p>
                        <p className="text-xs text-muted-foreground mb-1">{assessmentMetrics.topDistrict.regionName}</p>
                        <p className={`text-2xl font-bold mt-1 ${getPerformanceColor(classifyPerformance(assessmentMetrics.topDistrictScore))}`}>
                          {assessmentMetrics.topDistrictScore.toFixed(1)}%
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {classifyPerformance(assessmentMetrics.topDistrictScore)} • Rank: #{assessmentMetrics.topDistrict.nationalRank}
                        </p>
                      </div>
                    )}

                    <div className="bg-muted/20 rounded-lg p-4">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Regional Performance Tiers</p>
                      <div className="flex items-center justify-around">
                        <div className="text-center">
                          <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-1">
                            <span className="text-white font-bold text-sm">{assessmentMetrics.tierBreakdown.top25}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Top 25%</p>
                        </div>
                        <div className="text-center">
                          <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center mx-auto mb-1">
                            <span className="text-white font-bold text-sm">{assessmentMetrics.tierBreakdown.mid50}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Middle 50%</p>
                        </div>
                        <div className="text-center">
                          <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center mx-auto mb-1">
                            <span className="text-white font-bold text-sm">{assessmentMetrics.tierBreakdown.bottom25}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Bottom 25%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

               
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-3">Regional Performance Summary</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-border">
                        <tr className="text-left">
                          <th className="pb-2 font-medium text-muted-foreground">Rank</th>
                          <th className="pb-2 font-medium text-muted-foreground">Region</th>
                          <th className="pb-2 font-medium text-muted-foreground">Score</th>
                          <th className="pb-2 font-medium text-muted-foreground">Classification</th>
                          <th className="pb-2 font-medium text-muted-foreground">Districts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assessmentRegionData.map((region, idx) => (
                          <tr key={region.code} className="border-b border-border/50">
                            <td className="py-2 text-muted-foreground">#{idx + 1}</td>
                            <td className="py-2 font-medium">{region.name}</td>
                            <td className="py-2 font-semibold">{region.score.toFixed(1)}%</td>
                            <td className="py-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getPerformanceBadgeClass(region.classification)}`}>
                                {region.classification}
                              </span>
                            </td>
                            <td className="py-2 text-muted-foreground">{region.districts}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : null}
          </div> 

          {/* Combined Chart: Activity Plan vs Executed by Region */}
          <div className="metric-card mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Activity Plan vs Executed by Region</h3>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(215, 60%, 22%)" }}></div>
                  <span>Planned</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(142, 70%, 45%)" }}></div>
                  <span>Executed (Projects + Programmes)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "hsl(38, 92%, 50%)" }}></div>
                  <span>Execution Rate %</span>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={planVsExecutedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                <XAxis
                  dataKey="region"
                  tick={{ fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="plan"
                  name="Planned Activities"
                  fill="hsl(215, 60%, 22%)"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  yAxisId="left"
                  dataKey="executed"
                  name="Executed Activities"
                  fill="hsl(142, 70%, 45%)"
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="executionRate"
                  name="Execution Rate %"
                  stroke="hsl(38, 92%, 50%)"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Pie Charts Row */}
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <DevelopmentDimensionPieCard
              title="Projects by Development Dimension"
              data={projectsByDimension}
              tooltipContent={<ProjectsPieTooltip />}
              emptyMessage="No project dimension data available"
            />
            <DevelopmentDimensionPieCard
              title="Activity Plans by Development Dimension"
              data={aapByDimension}
              tooltipContent={<AAPPieTooltip />}
              emptyMessage="No activity plan dimension data available"
            />
          </div>

          {/* Other Regional Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Projects by Region */}
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Projects by Region</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={projectsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                  <XAxis dataKey="region" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="hsl(174, 60%, 40%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Programmes by Region */}
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Programmes by Region</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={programmesData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                  <XAxis dataKey="region" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="hsl(38, 92%, 50%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Meetings by Region */}
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">Meetings by Region</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={meetingsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                  <XAxis dataKey="region" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="hsl(262, 52%, 55%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* IGF Collected by Region */}
            <div className="metric-card">
              <h3 className="font-semibold text-sm mb-4">IGF Collected by Region (GHS)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={igfData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 20%, 88%)" />
                  <XAxis dataKey="region" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatIgfCurrency(value)} />
                  <Bar dataKey="totalAmount" fill="hsl(142, 70%, 45%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPreview && previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-[90vw] max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-accent" />
                <h3 className="font-semibold">Dashboard Preview - {selectedYear}</h3>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadFromPreview}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download as PNG
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={closePreview}
                >
                  Close
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <img
                src={previewUrl}
                alt="Dashboard Preview"
                className="w-full h-auto rounded-lg shadow-lg"
                style={{ maxHeight: 'calc(90vh - 80px)', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
